import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { pt } from 'date-fns/locale';
import { Plus, Trash2, Calendar as CalendarIcon, Clock, MapPin, Phone, User, Edit, ChevronLeft, ChevronRight, Send, Mail, CheckCircle, Palette, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const DIENST_KLEUREN = ['#f59e0b', '#8b5cf6', '#ec4899', '#3b82f6', '#ef4444', '#84cc16', '#f97316', '#06b6d4', '#a855f7', '#e11d48', '#10b981', '#64748b'];

const toDateStr = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const MultiDateCalendar = ({ dates, onChange, disablePast = false, defaultMonth }) => (
  <div className="border border-gray-200 rounded-lg bg-white inline-block" data-testid="multi-date-calendar">
    <Calendar
      mode="multiple"
      selected={dates.map(d => new Date(d + 'T12:00:00'))}
      onSelect={(selected) => onChange((selected || []).map(toDateStr).sort())}
      disabled={disablePast ? { before: new Date() } : undefined}
      defaultMonth={defaultMonth}
      locale={pt}
      classNames={{
        day_selected: 'bg-[#3da5a5] text-white hover:bg-[#2d8585] hover:text-white focus:bg-[#3da5a5] focus:text-white',
        day_today: 'bg-gray-200 text-gray-900 aria-selected:bg-[#3da5a5] aria-selected:text-white',
      }}
    />
  </div>
);

const DienstenBeheer = ({ diensten, onRefresh }) => {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DIENST_KLEUREN[0]);

  useEffect(() => {
    setRows(diensten.map(d => ({ ...d })));
    const used = diensten.map(d => d.color);
    setNewColor(DIENST_KLEUREN.find(c => !used.includes(c)) || DIENST_KLEUREN[diensten.length % DIENST_KLEUREN.length]);
  }, [diensten]);

  const updateLocal = (id, field, value) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const persistName = async (row) => {
    const original = diensten.find(d => d.id === row.id);
    if (!original || original.name === row.name) return;
    if (!row.name.trim()) {
      toast.error('Naam mag niet leeg zijn');
      updateLocal(row.id, 'name', original.name);
      return;
    }
    try {
      await axiosInstance.put(`/diensten/${row.id}`, { name: row.name.trim() });
      toast.success('Dienst bijgewerkt');
      onRefresh();
    } catch {
      toast.error('Fout bij opslaan');
    }
  };

  const persistColor = async (row) => {
    const original = diensten.find(d => d.id === row.id);
    if (!original || original.color === row.color) return;
    try {
      await axiosInstance.put(`/diensten/${row.id}`, { color: row.color });
      onRefresh();
    } catch {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Vul een naam in');
      return;
    }
    try {
      await axiosInstance.post('/diensten', { name: newName.trim(), color: newColor });
      toast.success('Dienst toegevoegd');
      setNewName('');
      onRefresh();
    } catch {
      toast.error('Fout bij toevoegen');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deze dienst verwijderen? Bestaande agendamentos krijgen de standaardkleur.')) return;
    try {
      await axiosInstance.delete(`/diensten/${id}`);
      toast.success('Dienst verwijderd');
      onRefresh();
    } catch {
      toast.error('Fout bij verwijderen');
    }
  };

  return (
    <div className="space-y-4" data-testid="diensten-beheer">
      <div className="space-y-2">
        {rows.map(row => (
          <div key={row.id} className="flex items-center gap-2" data-testid={`dienst-row-${row.id}`}>
            <input
              type="color"
              value={row.color}
              onChange={(e) => updateLocal(row.id, 'color', e.target.value)}
              onBlur={() => persistColor(row)}
              className="w-9 h-9 rounded cursor-pointer border border-gray-200 p-0.5 bg-white shrink-0"
              title="Kleur wijzigen"
              data-testid={`dienst-color-${row.id}`}
            />
            <Input
              value={row.name}
              onChange={(e) => updateLocal(row.id, 'name', e.target.value)}
              onBlur={() => persistName(row)}
              className="bg-gray-50 border-gray-200 text-gray-800 flex-1"
              data-testid={`dienst-name-${row.id}`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleDelete(row.id)}
              className="text-red-500 border-gray-200 hover:bg-red-50 shrink-0"
              data-testid={`dienst-delete-${row.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-2">Nog geen diensten</p>
        )}
      </div>
      <div className="border-t border-gray-200 pt-4">
        <Label className="text-gray-800 mb-2 block text-sm">Nieuwe dienst</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="w-9 h-9 rounded cursor-pointer border border-gray-200 p-0.5 bg-white shrink-0"
            data-testid="dienst-new-color"
          />
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Naam van de dienst"
            className="bg-gray-50 border-gray-200 text-gray-800 flex-1"
            data-testid="dienst-new-name"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAdd}
            className="bg-[#3da5a5] hover:bg-[#2d8585] text-white shrink-0"
            data-testid="dienst-add-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ScheduleCalendar = ({ user }) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    client_name: '',
    client_address: '',
    client_phone: '',
    client_email: '',
    work_description: '',
    assigned_users: [],
    dienst_id: '',
  });
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [multiDates, setMultiDates] = useState([]);
  const [sendingConfirm, setSendingConfirm] = useState({});
  const [diensten, setDiensten] = useState([]);
  const [dienstFilter, setDienstFilter] = useState('all');
  const [manageDienstenOpen, setManageDienstenOpen] = useState(false);
  const [groupEditMode, setGroupEditMode] = useState(false);
  const [groupSchedules, setGroupSchedules] = useState([]);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateDates, setDuplicateDates] = useState([]);

  const handleSendConfirmation = async (schedule) => {
    const eind = resolveEindklant(schedule);
    let email = (schedule.client_email || eind.email || '').trim();
    const klantName = eind.label || schedule.client_name;
    if (!email) {
      email = (window.prompt(`E-mailadres van klant ${klantName} om de bevestiging naartoe te sturen:`, '') || '').trim();
      if (!email) return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Ongeldig e-mailadres');
      return;
    }
    if (!window.confirm(`Bevestiging sturen naar ${klantName} (${email})?`)) return;
    setSendingConfirm((s) => ({ ...s, [schedule.id]: true }));
    try {
      const r = await axiosInstance.post(`/schedule/${schedule.id}/send-confirmation`, { email });
      toast.success(`Bevestiging verzonden naar ${r.data.email}`);
      fetchSchedules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSendingConfirm((s) => ({ ...s, [schedule.id]: false }));
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchClients();
    fetchUsers(); // Todos precisam ver nomes dos funcionários
    fetchDiensten();
  }, []);

  const fetchDiensten = async () => {
    try {
      const response = await axiosInstance.get('/diensten');
      setDiensten(response.data);
    } catch (error) {
      console.error('Error fetching diensten');
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await axiosInstance.get('/schedule');
      setSchedules(response.data);
    } catch (error) {
      toast.error('Erro ao carregar agenda');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients/all');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  // Gerar dias do calendário
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Adicionar dias do mês anterior
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevMonthDay = new Date(year, month, -i);
      days.push({ date: prevMonthDay, isCurrentMonth: false });
    }
    
    // Adicionar dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Adicionar dias do próximo mês para completar a grade
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getSchedulesForDate = (date) => {
    const dateStr = formatDateLocal(date);
    return schedules
      .filter(s => s.date === dateStr)
      .filter(s => dienstFilter === 'all' || s.dienst_id === dienstFilter)
      .sort((a, b) => {
        // Ordenar por horário (time)
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
        return 0;
      });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setDetailsOpen(true);
  };

  const formatDateLocal = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleAddSchedule = (dateToUse = null) => {
    if (!user.is_admin) {
      toast.error('Apenas administradores podem editar a agenda');
      return;
    }
    const targetDate = dateToUse || selectedDate;
    setEditingSchedule(null);
    setSelectedClientId('');
    setSelectedAddressId('');
    setGroupEditMode(false);
    setGroupSchedules([]);
    setMultiDates(targetDate ? [formatDateLocal(targetDate)] : []);
    setFormData({
      date: targetDate ? formatDateLocal(targetDate) : '',
      time: '',
      client_name: '',
      client_address: '',
      client_phone: '',
      client_email: '',
      work_description: '',
      assigned_users: [],
      dienst_id: '',
    });
    setDetailsOpen(false);
    setAddEditOpen(true);
  };

  const handleEditSchedule = (schedule) => {
    if (!user.is_admin) {
      toast.error('Apenas administradores podem editar a agenda');
      return;
    }
    setEditingSchedule(schedule);
    // Agendamentos do mesmo cliente (mesmo nome + endereço) de hoje em diante
    const todayStr = formatDateLocal(new Date());
    const normTxt = (v) => (v || '').trim().toLowerCase();
    const siblings = schedules.filter(s =>
      normTxt(s.client_name) === normTxt(schedule.client_name) &&
      normTxt(s.client_address) === normTxt(schedule.client_address) &&
      s.date >= todayStr
    );
    setGroupSchedules(siblings);
    setGroupEditMode(false);
    setMultiDates([]);
    setFormData({
      date: schedule.date,
      time: schedule.time,
      client_name: schedule.client_name,
      client_address: schedule.client_address,
      client_phone: schedule.client_phone,
      client_email: schedule.client_email || '',
      work_description: schedule.work_description || '',
      assigned_users: schedule.assigned_users || [],
      dienst_id: schedule.dienst_id || '',
    });
    
    // Find matching client (case-insensitive, trimmed)
    const schedClientName = (schedule.client_name || '').trim().toLowerCase();
    const clientMatch = clients.find(c => (c.name || '').trim().toLowerCase() === schedClientName);
    if (clientMatch) {
      setSelectedClientId(clientMatch.id);
      const addresses = clientMatch.addresses || [];
      const schedAddr = (schedule.client_address || '').trim().toLowerCase();
      const addrMatch = addresses.find(a => (a.address || '').trim().toLowerCase() === schedAddr);
      if (addrMatch) {
        setSelectedAddressId(addrMatch.id);
      } else {
        setSelectedAddressId('other');
      }
    } else {
      setSelectedClientId('new');
      setSelectedAddressId('');
    }
    
    setDetailsOpen(false);
    setAddEditOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar endereço obrigatório quando cliente tem múltiplos endereços
    const selectedClient = clients.find(c => c.name === formData.client_name);
    if (selectedClient && selectedClient.addresses && selectedClient.addresses.length > 0 && !selectedAddressId && !formData.client_address) {
      toast.error('Selecione um endereço para este cliente');
      return;
    }
    
    try {
      if (editingSchedule && groupEditMode) {
        // Edição em grupo: vários dias do mesmo cliente de uma vez
        if (multiDates.length === 0) {
          toast.error('Selecione pelo menos uma data');
          return;
        }
        const removedCount = [...new Set(groupSchedules.map(s => s.date))].filter(d => !multiDates.includes(d)).length;
        if (removedCount > 0 && !window.confirm(`${removedCount} dia(s) será(ão) removido(s) da agenda. Continuar?`)) return;
        const payload = {
          ids: groupSchedules.map(s => s.id),
          dates: multiDates,
          time: formData.time,
          client_name: formData.client_name,
          client_address: formData.client_address,
          client_phone: formData.client_phone,
          client_email: formData.client_email,
          work_description: formData.work_description,
          assigned_users: formData.assigned_users,
          dienst_id: formData.dienst_id || null,
        };
        const res = await axiosInstance.put('/schedule/group', payload);
        toast.success(`Agendamentos atualizados (${res.data.updated} editados, ${res.data.created} novos, ${res.data.deleted} removidos)`);
      } else if (editingSchedule) {
        await axiosInstance.put(`/schedule/${editingSchedule.id}`, formData);
        toast.success('Agendamento atualizado!');
      } else if (multiDates.length > 1) {
        // Multi-date creation
        const payload = {
          dates: multiDates,
          time: formData.time,
          client_name: formData.client_name,
          client_address: formData.client_address,
          client_phone: formData.client_phone,
          work_description: formData.work_description,
          assigned_users: formData.assigned_users,
          dienst_id: formData.dienst_id || null,
        };
        const res = await axiosInstance.post('/schedule/multi', payload);
        toast.success(`${res.data.count} agendamentos criados!`);
      } else {
        // Single date - use first date from multiDates or formData.date
        const singleDate = multiDates[0] || formData.date;
        await axiosInstance.post('/schedule', { ...formData, date: singleDate });
        toast.success('Agendamento criado!');
      }
      setAddEditOpen(false);
      setSelectedClientId('');
      setSelectedAddressId('');
      setMultiDates([]);
      setGroupEditMode(false);
      setGroupSchedules([]);
      fetchSchedules();
    } catch (error) {
      toast.error('Erro ao salvar agendamento');
    }
  };

  const enableGroupEdit = () => {
    const dates = [...new Set(groupSchedules.map(s => s.date))].sort();
    setMultiDates(dates);
    setGroupEditMode(true);
  };

  const handleDeleteGroup = async () => {
    if (!user.is_admin) {
      toast.error('Apenas administradores podem deletar');
      return;
    }
    const count = groupSchedules.length;
    const klantName = editingSchedule?.client_name || 'deste cliente';
    if (!window.confirm(`Apagar TODOS os ${count} agendamento${count !== 1 ? 's' : ''} de ${klantName} (de hoje em diante)?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      const res = await axiosInstance.delete('/schedule/group', { data: { ids: groupSchedules.map(s => s.id) } });
      toast.success(`${res.data.deleted} agendamento${res.data.deleted !== 1 ? 's' : ''} apagado${res.data.deleted !== 1 ? 's' : ''}!`);
      setAddEditOpen(false);
      setSelectedClientId('');
      setSelectedAddressId('');
      setMultiDates([]);
      setGroupEditMode(false);
      setGroupSchedules([]);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apagar agendamentos');
    }
  };

  const removeMultiDate = (dateStr) => {
    setMultiDates(prev => prev.filter(d => d !== dateStr));
  };

  const openDuplicateModal = (schedule) => {
    setDuplicateSource(schedule);
    setDuplicateDates([]);
    setDuplicateOpen(true);
  };

  const handleDuplicate = async () => {
    if (!duplicateSource || duplicateDates.length === 0) {
      toast.error('Selecione pelo menos uma data');
      return;
    }
    try {
      const res = await axiosInstance.post('/schedule/duplicate', {
        source_id: duplicateSource.id,
        dates: duplicateDates,
      });
      toast.success(`${res.data.created} agendamento${res.data.created !== 1 ? 's' : ''} criado${res.data.created !== 1 ? 's' : ''}!`);
      setDuplicateOpen(false);
      setDuplicateSource(null);
      setDuplicateDates([]);
      setDetailsOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao duplicar agendamento');
    }
  };

  const handleDelete = async (id) => {
    if (!user.is_admin) {
      toast.error('Apenas administradores podem deletar');
      return;
    }
    
    if (!window.confirm('Deseja deletar este agendamento?')) return;

    try {
      await axiosInstance.delete(`/schedule/${id}`);
      toast.success('Agendamento deletado!');
      fetchSchedules();
      setDetailsOpen(false);
    } catch (error) {
      toast.error('Erro ao deletar agendamento');
    }
  };

  const days = getDaysInMonth(currentDate);
  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  // Resolve the eindklant (real klant) data from a stored schedule.
  // Old schedules stored bedrijfsnaam (Cardoso Bouw) as client_name. We try to
  // upgrade the display by matching the address against the clients tree —
  // if we find a row, we return {label, email, phone, bedrijf}.
  const resolveEindklant = (schedule) => {
    const out = {
      label: schedule.client_name || '',
      bedrijf: '',
      email: schedule.client_email || '',
      phone: schedule.client_phone || '',
    };
    const schedAddr = (schedule.client_address || '').trim().toLowerCase();
    if (!schedAddr) return out;
    for (const c of clients) {
      const addrs = c.addresses || [];
      const m = addrs.find(a => (a.address || '').trim().toLowerCase() === schedAddr);
      if (m) {
        if (m.label) out.label = m.label;
        out.bedrijf = c.name;
        if (!out.email && m.email) out.email = m.email;
        if (!out.email && c.email) out.email = c.email;
        if (!out.phone && m.phone) out.phone = m.phone;
        if (!out.phone && c.phone) out.phone = c.phone;
        break;
      }
    }
    return out;
  };

  const getDienst = (schedule) => {
    if (!schedule.dienst_id) return null;
    return diensten.find(d => d.id === schedule.dienst_id) || null;
  };

  const selectedDaySchedules = selectedDate ? getSchedulesForDate(selectedDate) : [];
  const today = formatDateLocal(new Date());

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-800">Agenda</h1>
        {user.is_admin && (
          <Button
            onClick={() => handleAddSchedule(new Date())}
            className="bg-[#3da5a5] hover:bg-[#2d8585] text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Agendamento
          </Button>
        )}
      </div>

      {/* Filtro e legenda de diensten */}
      <div className="flex flex-wrap items-center gap-2" data-testid="dienst-filter-bar">
        <button
          type="button"
          onClick={() => setDienstFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            dienstFilter === 'all'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="dienst-filter-all"
        >
          Alle
        </button>
        {diensten.map(d => {
          const active = dienstFilter === d.id;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setDienstFilter(active ? 'all' : d.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                active ? 'text-white' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
              style={active ? { backgroundColor: d.color, borderColor: d.color } : {}}
              data-testid={`dienst-filter-${d.id}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: active ? '#ffffff' : d.color }}
              />
              {d.name}
            </button>
          );
        })}
        {user.is_admin && (
          <button
            type="button"
            onClick={() => setManageDienstenOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
            data-testid="manage-diensten-btn"
          >
            <Palette className="w-3.5 h-3.5" />
            Diensten beheren
          </button>
        )}
      </div>

      {/* Calendário */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevMonth}
              className="text-gray-800 border-white/30"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <CardTitle className="text-gray-800 text-2xl">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextMonth}
              className="text-gray-800 border-white/30"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Nomes dos dias */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-gray-500 font-semibold text-sm py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Grade do calendário */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const daySchedules = getSchedulesForDate(day.date);
              const dateStr = formatDateLocal(day.date);
              const isToday = dateStr === today;
              
              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(day.date)}
                  className={`
                    min-h-[80px] p-2 rounded-lg border transition-all duration-200
                    ${day.isCurrentMonth ? 'bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-100'}
                    ${isToday ? 'ring-2 ring-[#3da5a5]' : ''}
                    hover:bg-gray-100 hover:border-white/40
                  `}
                >
                  <div className={`text-sm font-semibold mb-1 ${day.isCurrentMonth ? 'text-gray-800' : 'text-gray-300'}`}>
                    {day.date.getDate()}
                  </div>
                  {daySchedules.length > 0 && (
                    <div className="space-y-1">
                      {daySchedules.slice(0, 2).map(schedule => {
                        const eind = resolveEindklant(schedule);
                        const dienst = getDienst(schedule);
                        return (
                          <div
                            key={schedule.id}
                            className="text-xs text-white rounded px-1 py-0.5 truncate"
                            style={{ backgroundColor: dienst ? dienst.color : 'rgba(61, 165, 165, 0.8)' }}
                            title={dienst ? `${dienst.name} — ${eind.label || schedule.client_name}` : (eind.label || schedule.client_name)}
                          >
                            {schedule.time} - {eind.label || schedule.client_name}
                          </div>
                        );
                      })}
                      {daySchedules.length > 2 && (
                        <div className="text-xs text-[#3da5a5] font-semibold">
                          +{daySchedules.length - 2} mais
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Detalhes do Dia */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">
              {selectedDate && `${selectedDate.getDate()} de ${monthNames[selectedDate.getMonth()]}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedDaySchedules.length === 0 ? (
              <p className="text-gray-400 text-center py-8">Nenhum agendamento para este dia</p>
            ) : (
              selectedDaySchedules.map(schedule => {
                const eind = resolveEindklant(schedule);
                return (
                <Card key={schedule.id} className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-[#3da5a5] font-semibold text-sm">HORÁRIO</p>
                          <p className="text-gray-800 text-lg font-bold">{schedule.time}</p>
                          {(() => {
                            const dienst = getDienst(schedule);
                            return dienst ? (
                              <span
                                className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
                                style={{ backgroundColor: dienst.color }}
                                data-testid={`schedule-dienst-badge-${schedule.id}`}
                              >
                                {dienst.name}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        {user.is_admin && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSendConfirmation(schedule)}
                              disabled={!!sendingConfirm[schedule.id]}
                              className={schedule.confirmation_sent_at ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-none' : 'bg-amber-500 hover:bg-amber-600 text-white border-none'}
                              title={schedule.confirmation_sent_at ? `Verzonden naar ${schedule.confirmation_sent_to} op ${new Date(schedule.confirmation_sent_at).toLocaleString('nl-NL')}` : 'Bevestiging per e-mail naar klant versturen'}
                              data-testid={`calendar-send-confirmation-${schedule.id}`}
                            >
                              {schedule.confirmation_sent_at ? <CheckCircle className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditSchedule(schedule)}
                              className="bg-blue-600 hover:bg-blue-700 text-white border-none"
                              data-testid={`schedule-edit-btn-${schedule.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDuplicateModal(schedule)}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white border-none"
                              title="Duplicar para outras datas (trabalho recorrente)"
                              data-testid={`schedule-duplicate-btn-${schedule.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(schedule.id)}
                              className="bg-red-600 hover:bg-red-700 text-white border-none"
                              data-testid={`schedule-delete-btn-${schedule.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>


                      <div>
                        <p className="text-gray-500 text-sm">KLANT</p>
                        <p className="text-gray-800 font-semibold">{eind.label || schedule.client_name}</p>
                        {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || schedule.client_name || '').toLowerCase() && (
                          <p className="text-gray-500 text-[11px]">Bedrijf: {eind.bedrijf}</p>
                        )}
                      </div>
                      
                      <div>
                        <p className="text-gray-500 text-sm">ENDEREÇO</p>
                        <p className="text-gray-800">{schedule.client_address}</p>
                      </div>
                      
                      {schedule.client_phone && (
                        <div>
                          <p className="text-gray-500 text-sm">TELEFONE</p>
                          <p className="text-gray-800">{schedule.client_phone}</p>
                        </div>
                      )}
                      
                      {schedule.work_description && (
                        <div>
                          <p className="text-gray-500 text-sm">DESCRIÇÃO</p>
                          <p className="text-gray-800">{schedule.work_description}</p>
                        </div>
                      )}
                      
                      {schedule.assigned_users && schedule.assigned_users.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-sm mb-2">FUNCIONÁRIOS</p>
                          <div className="flex flex-wrap gap-2">
                            {schedule.assigned_users.map(userId => {
                              const assignedUser = users.find(u => u.id === userId);
                              return (
                                <div key={userId} className="flex items-center gap-2">
                                  <span className="bg-[#3da5a5]/20 text-[#3da5a5] px-3 py-1 rounded-full text-sm">
                                    {assignedUser?.name || 'Usuário'}
                                  </span>
                                  {user.is_admin && assignedUser && (
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        const [year, month, day] = schedule.date.split('-');
                                        const dateStr = `${day}/${month}/${year}`;
                                        const message = `*Agendamento - Cardoso Bouw*%0A%0A` +
                                          `📅 *Data:* ${dateStr}%0A` +
                                          `🕐 *Horário:* ${schedule.time}%0A` +
                                          `👤 *Cliente:* ${schedule.client_name}%0A` +
                                          `📍 *Endereço:* ${schedule.client_address}%0A` +
                                          `📞 *Telefone:* ${schedule.client_phone}%0A` +
                                          (schedule.work_description ? `📝 *Descrição:* ${schedule.work_description}%0A` : '') +
                                          `%0A_Enviado via Dashboard_`;
                                        const phone = assignedUser.phone_number?.replace(/\D/g, '') || '';
                                        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                                      }}
                                      className="bg-[#25D366] hover:bg-[#1da851] h-7 px-2"
                                    >
                                      <Send className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })
            )}
            
            {user.is_admin && (
              <Button
                onClick={() => handleAddSchedule(selectedDate)}
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Agendamento para Este Dia
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Adicionar/Editar */}
      <Dialog open={addEditOpen} onOpenChange={setAddEditOpen}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">
              {editingSchedule ? (groupEditMode ? 'Editar Vários Agendamentos' : 'Editar Agendamento') : 'Novo Agendamento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {editingSchedule && groupSchedules.length > 1 && !groupEditMode && (
              <div className="bg-[#3da5a5]/10 border border-[#3da5a5]/30 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="group-edit-banner">
                <p className="text-sm text-gray-700">
                  Este cliente tem <strong>{[...new Set(groupSchedules.map(s => s.date))].length} agendamentos</strong> de hoje em diante.
                </p>
                <Button
                  type="button"
                  size="sm"
                  onClick={enableGroupEdit}
                  className="bg-[#3da5a5] hover:bg-[#2d8585] text-white shrink-0"
                  data-testid="enable-group-edit-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Editar todos os dias
                </Button>
              </div>
            )}
            {groupEditMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="group-edit-active">
                <p className="text-sm text-amber-800 flex-1 min-w-0">
                  Editando <strong>{multiDates.length} dia{multiDates.length !== 1 ? 's' : ''}</strong> deste cliente de uma vez. Clique nos dias do calendário para adicionar ou remover.
                </p>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={handleDeleteGroup}
                    className="text-xs font-medium text-red-700 hover:text-red-900 underline"
                    data-testid="delete-group-btn"
                  >
                    Apagar todos os dias
                  </button>
                  <span className="text-amber-300">|</span>
                  <button
                    type="button"
                    onClick={() => { setGroupEditMode(false); setMultiDates([]); }}
                    className="text-xs underline text-amber-700 hover:text-amber-900"
                    data-testid="disable-group-edit-btn"
                  >
                    Editar só este dia
                  </button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                {editingSchedule && !groupEditMode ? (
                  <>
                    <Label className="text-gray-800 mb-2 block">Data</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="bg-gray-50 border-white/30 text-gray-800"
                    />
                  </>
                ) : (
                  <>
                    <Label className="text-gray-800 mb-2 block">
                      Datas ({multiDates.length} selecionada{multiDates.length !== 1 ? 's' : ''}) — clique nos dias
                    </Label>
                    <MultiDateCalendar
                      dates={multiDates}
                      onChange={setMultiDates}
                      disablePast={groupEditMode}
                      defaultMonth={multiDates[0] ? new Date(multiDates[0] + 'T12:00:00') : currentDate}
                    />
                  </>
                )}
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Horário</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
                {(!editingSchedule || groupEditMode) && multiDates.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3 max-h-44 overflow-y-auto content-start" data-testid="selected-dates-chips">
                    {multiDates.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 bg-[#3da5a5]/10 text-[#3da5a5] px-2 py-1 rounded text-xs font-medium h-fit">
                        {new Date(d + 'T12:00:00').toLocaleDateString('pt-PT')}
                        <button type="button" onClick={() => removeMultiDate(d)} className="text-[#3da5a5] hover:text-red-500 ml-1">&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Bedrijf</Label>
              <Select
                value={selectedClientId}
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  if (client) {
                    setSelectedClientId(value);
                    setSelectedAddressId('');
                    setFormData({
                      ...formData,
                      // Provisional: gebruik bedrijfsnaam totdat klant een adres-label kiest
                      client_name: client.name,
                      client_phone: client.phone || '',
                      client_email: client.email || '',
                      client_address: '', // wacht op klant-adres-selectie
                    });
                  } else if (value === 'new') {
                    setSelectedClientId('new');
                    setSelectedAddressId('');
                    setFormData({
                      ...formData,
                      client_name: '',
                      client_phone: '',
                      client_email: '',
                      client_address: '',
                    });
                  }
                }}
              >
                <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                  <SelectValue placeholder="Selecione o bedrijf (empresa) ou crie novo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ Novo Bedrijf</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedClientId === 'new' && (
              <div>
                <Label className="text-gray-800 mb-2 block">Nome do Klant</Label>
                <Input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="Digite o nome do klant"
                />
              </div>
            )}

            {/* Se cliente selecionado tiver múltiplos endereços, mostrar dropdown */}
            {selectedClientId && selectedClientId !== 'new' && (() => {
              const client = clients.find(c => c.id === selectedClientId);
              const addresses = client?.addresses || [];

              if (addresses.length > 0) {
                return (
                  <div>
                    <Label className="text-gray-800 mb-2 block">Klant (endereço/label)</Label>
                    <Select
                      value={selectedAddressId}
                      onValueChange={(value) => {
                        const address = addresses.find(a => a.id === value);
                        if (address) {
                          setSelectedAddressId(value);
                          // Hier: address.label is de eindklant — gebruik die als client_name
                          setFormData({
                            ...formData,
                            client_name: address.label || client.name,
                            client_address: address.address,
                            client_email: address.email || client.email || '',
                            client_phone: address.phone || client.phone || '',
                          });
                        } else if (value === 'other') {
                          setSelectedAddressId('other');
                          // Behoud huidige naam/adres
                        }
                      }}
                    >
                      <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                        <SelectValue placeholder="Selecione o klant (label ou endereço)" />
                      </SelectTrigger>
                      <SelectContent>
                        {addresses.map(addr => (
                          <SelectItem key={addr.id} value={addr.id}>
                            {addr.label ? `${addr.label} — ${addr.address}` : addr.address}
                            {addr.is_primary ? ' (Principal)' : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="other">Outro endereço</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-500 mt-1">O nome do label é o nome real do klant (eindklant).</p>
                  </div>
                );
              }
              return null;
            })()}

            {/* Campo de endereço manual se: novo cliente OU "outro endereço" OU cliente sem endereços múltiplos */}
            {(selectedClientId === 'new' || selectedAddressId === 'other' || 
              (selectedClientId && selectedClientId !== 'new' && 
               !(clients.find(c => c.id === selectedClientId)?.addresses?.length > 0))) && (
              <div>
                <Label className="text-gray-800 mb-2 block">Endereço</Label>
                <Input
                  type="text"
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="Digite o endereço"
                />
              </div>
            )}

            <div>
              <Label className="text-gray-800 mb-2 block">Telefone</Label>
              <Input
                type="text"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Descrição do Trabalho</Label>
              <Input
                type="text"
                value={formData.work_description}
                onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Dienst</Label>
              <Select
                value={formData.dienst_id || 'none'}
                onValueChange={(v) => setFormData({ ...formData, dienst_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800" data-testid="schedule-dienst-select">
                  <SelectValue placeholder="Selecteer dienst" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen dienst</SelectItem>
                  {diensten.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full inline-block shrink-0" style={{ backgroundColor: d.color }} />
                        {d.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


            {user.is_admin && users.length > 0 && (
              <div>
                <Label className="text-gray-800 mb-2 block">Funcionários</Label>
                <Select
                  value={formData.assigned_users[0] || ''}
                  onValueChange={(value) => {
                    const newUsers = formData.assigned_users.includes(value)
                      ? formData.assigned_users.filter(id => id !== value)
                      : [...formData.assigned_users, value];
                    setFormData({ ...formData, assigned_users: newUsers });
                  }}
                >
                  <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                    <SelectValue placeholder="Selecione funcionários" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {formData.assigned_users.includes(u.id) ? '✓' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.assigned_users.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.assigned_users.map(userId => {
                      const assignedUser = users.find(u => u.id === userId);
                      return (
                        <span
                          key={userId}
                          className="bg-[#3da5a5]/20 text-[#3da5a5] px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {assignedUser?.name}
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              assigned_users: formData.assigned_users.filter(id => id !== userId)
                            })}
                            className="text-red-400 hover:text-red-300"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold py-6"
              data-testid="schedule-save-btn"
            >
              {editingSchedule
                ? (groupEditMode ? `Atualizar ${multiDates.length} agendamento${multiDates.length !== 1 ? 's' : ''}` : 'Atualizar')
                : 'Criar Agendamento'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Gerenciar Diensten */}
      <Dialog open={manageDienstenOpen} onOpenChange={(open) => { setManageDienstenOpen(open); if (!open) { fetchDiensten(); } }}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">Diensten beheren</DialogTitle>
          </DialogHeader>
          <DienstenBeheer diensten={diensten} onRefresh={fetchDiensten} />
        </DialogContent>
      </Dialog>

      {/* Dialog de Duplicar Agendamento */}
      <Dialog open={duplicateOpen} onOpenChange={(open) => { setDuplicateOpen(open); if (!open) { setDuplicateSource(null); setDuplicateDates([]); } }}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="duplicate-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">Duplicar Agendamento</DialogTitle>
          </DialogHeader>
          {duplicateSource && (
            <div className="space-y-4">
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm text-cyan-900">
                <p className="font-semibold mb-1">Origem:</p>
                <p>{duplicateSource.client_name} — {duplicateSource.time}</p>
                <p className="text-xs text-cyan-700 mt-0.5">{duplicateSource.client_address}</p>
                {duplicateSource.work_description && (
                  <p className="text-xs text-cyan-700 mt-0.5 italic">{duplicateSource.work_description}</p>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Selecione as novas datas no calendário abaixo. Mesmo horário, klant, dienst e funcionários serão copiados.
              </p>
              <div data-testid="duplicate-multi-date-calendar">
                <MultiDateCalendar
                  dates={duplicateDates}
                  onChange={setDuplicateDates}
                  disablePast={true}
                />
              </div>
              {duplicateDates.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="duplicate-selected-chips">
                  {duplicateDates.map(d => (
                    <span key={d} className="inline-flex items-center gap-1 bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded-full text-xs">
                      {d}
                      <button
                        type="button"
                        onClick={() => setDuplicateDates(prev => prev.filter(x => x !== d))}
                        className="text-cyan-600 hover:text-cyan-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setDuplicateOpen(false)}
                  variant="outline"
                  className="flex-1"
                  data-testid="duplicate-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleDuplicate}
                  disabled={duplicateDates.length === 0}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white disabled:bg-gray-300"
                  data-testid="duplicate-confirm-btn"
                >
                  Duplicar para {duplicateDates.length} data{duplicateDates.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleCalendar;
